import React from 'react';
import { motion } from 'framer-motion';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/utils/cn';

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
    icon?: React.ReactNode;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  variant?: 'full' | 'compact';
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
  secondaryAction,
  className,
  variant = 'full',
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        'flex flex-col items-center justify-center text-center',
        variant === 'full' ? 'py-20 px-6' : 'py-10 px-4',
        className,
      )}
    >
      <div className="relative mb-8">
        {/* Glow effect */}
        <div className="absolute inset-x-0 -bottom-2 h-10 w-full bg-green-500/20 blur-3xl rounded-full scale-150 animate-pulse" />

        {/* Icon Container */}
        <div className="relative z-10 p-6 bg-white dark:bg-zinc-800 rounded-3xl shadow-xl border border-gray-100 dark:border-white/5">
          <Icon size={variant === 'full' ? 48 : 32} className="text-green-500" />
        </div>
      </div>

      <h3
        className={cn(
          'font-bold text-gray-900 dark:text-white mb-2',
          variant === 'full' ? 'text-2xl' : 'text-lg',
        )}
      >
        {title}
      </h3>
      <p className="max-w-md text-gray-500 dark:text-zinc-400 mb-8 leading-relaxed text-balance">
        {description}
      </p>

      {(action || secondaryAction) && (
        <div className="flex flex-col sm:flex-row gap-3">
          {action && (
            <button
              onClick={action.onClick}
              className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold shadow-lg shadow-green-600/20 transition-all active:scale-95 flex items-center justify-center gap-2"
            >
              {action.icon}
              {action.label}
            </button>
          )}
          {secondaryAction && (
            <button
              onClick={secondaryAction.onClick}
              className="px-6 py-3 bg-gray-100 dark:bg-white/5 text-gray-700 dark:text-zinc-300 rounded-xl font-bold hover:bg-gray-200 dark:hover:bg-white/10 transition-all active:scale-95"
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}
    </motion.div>
  );
};
